/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState, useEffect } from "react";

import {
    Box,
    lighten,
    darken,
    useTheme,
    Divider,
    Tooltip,
    Stack,
    Typography,
    Autocomplete,
    IconButton,
    TextField,
    CircularProgress,
    Grid,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";

import { DetailedUser, FullAssignment, FullTechnicianEntry, User } from "../../../utils/Interfaces";

import { assignTechnician, setLeadTechnician, unassignTechnician, updateAssignments } from "./technicianEntryFunctions";
import { getAllTechnicians } from "../workOrderFunctions";
import { filterCollection } from "../../../utils/Functions";
import { useAppDispatch } from "../../../app/hooks";

export interface TechnicianEntryAssignmentsProps {
    editing: boolean;
    technicianEntry: FullTechnicianEntry;
}

const TechnicianEntryAssignments = (props: TechnicianEntryAssignmentsProps) => {
    const { editing, technicianEntry } = props;

    const dispatch = useAppDispatch();

    const [assignments, setAssignments] = useState<FullAssignment[]>([]);
    const [allTechnicians, setAllTechnicians] = useState<DetailedUser[]>([]);

    const [isLoading, setLoading] = useState<boolean>(false);
    const theme = useTheme();

    function reloadAssignees() {
        updateAssignments(technicianEntry, dispatch, setAssignments, setLoading);
    }

    useEffect(() => {
        if (editing) {
            getAllTechnicians(dispatch, setAllTechnicians);
        }
        reloadAssignees();
    }, [editing, technicianEntry, dispatch]);

    if (isLoading) {
        return (
            <Box>
                <Divider sx={{ mt: 2 }}>
                    <Typography variant="h5">{`Assignees (${assignments.length})`}</Typography>
                </Divider>
                <Stack direction="row" justifyContent="center" sx={{ width: "100%" }}>
                    <CircularProgress size={40} sx={{ mt: 5 }} />
                </Stack>
            </Box>
        );
    }

    return (
        <Box>
            <Divider sx={{ mt: 2 }}>
                <Typography variant="h5">{`Assignees (${assignments.length})`}</Typography>
            </Divider>

            <Box marginBottom={"20px"} />

            {editing && (
                <Autocomplete
                    disabled={!editing}
                    disablePortal
                    onChange={(_event, value) =>
                        assignTechnician(value as DetailedUser, technicianEntry, dispatch, reloadAssignees, setLoading)
                    }
                    id="assignee"
                    title="Lookup Person to Assign"
                    getOptionKey={(option: User) => option.id}
                    options={filterCollection<User, number>(
                        allTechnicians,
                        assignments.map((assignment: FullAssignment) => assignment.technician),
                        (user: User) => user.id
                    )}
                    blurOnSelect={true}
                    autoHighlight={true}
                    getOptionLabel={(option: User) => `${option.first_name} ${option.last_name}`}
                    renderInput={(params) => <TextField {...params} fullWidth size="small" label="Lookup Person to Assign" />}
                />
            )}

            {assignments.length === 0 && (
                <Typography textAlign="center" sx={{ mt: 2 }}>
                    No technicians assigned.
                </Typography>
            )}

            {assignments.length > 0 &&
                assignments.map((entry: FullAssignment, key: number) => {
                    let background = lighten(theme.palette.background.paper, 0.07);

                    if (key % 2 === 0) {
                        background = darken(background, 0.05);
                    }
                    return (
                        <Grid
                            container
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{
                                paddingLeft: 25,
                                paddingRight: 25,
                                background: background,
                            }}
                            key={key}
                        >
                            <Box marginBottom={"10px"} marginTop={"10px"}>
                                <Typography sx={{ fontSize: 18 }} color="text.primary">
                                    {`${entry.technician.first_name} ${entry.technician.last_name}`}
                                </Typography>
                                <Box sx={{ opacity: 0.5, fontSize: 12 }}>{`@${entry.technician.username}`}</Box>
                            </Box>
                            <Box>
                                {entry.lead && (
                                    <Tooltip title="Lead Assignee">
                                        <IconButton onClick={() => {}} size="large" disabled={!editing}>
                                            <StarIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {!entry.lead && (
                                    <Tooltip title="Set As Lead Assignee">
                                        <IconButton
                                            onClick={() =>
                                                setLeadTechnician(
                                                    entry.technician,
                                                    technicianEntry,
                                                    dispatch,
                                                    reloadAssignees,
                                                    setLoading
                                                )
                                            }
                                            size="large"
                                            disabled={!editing}
                                        >
                                            <StarBorderIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {editing && (
                                    <Tooltip title="Remove Assignee">
                                        <IconButton
                                            onClick={() =>
                                                unassignTechnician(
                                                    entry.technician,
                                                    technicianEntry,
                                                    dispatch,
                                                    reloadAssignees,
                                                    setLoading
                                                )
                                            }
                                            size="large"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Grid>
                    );
                })}
            <Box sx={{ marginBottom: "20px" }} />
        </Box>
    );
};

export default TechnicianEntryAssignments;
